import { getInboxUnreadCount } from '@services/inbox.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSocket } from './useSocket';

/**
 * Org-wide unread message count for the sidebar Inbox badge.
 *
 * Source of truth = backend `/inbox/unread-count` which aggregates
 * `SUM(unread_count)` across every conversation in the org. The hook:
 *  - fetches on mount + every 60s (cheap aggregate query)
 *  - invalidates on every realtime `inbox:new_message` event so the
 *    badge bumps within ~100 ms of the message arriving
 *  - relies on React Query's natural refetch when the user navigates
 *    back to a page that uses this hook
 *
 * The backend `getConversation` zeroes `unread_count` on read, so
 * after the user opens any conversation the next refetch (triggered
 * by InboxPage on detail mount, or the 60s poll) brings the badge
 * down naturally.
 */
export function useInboxUnreadCount(): number {
  const qc = useQueryClient();
  const { on, off } = useSocket();

  const { data } = useQuery({
    queryKey: ['inbox', 'unread-count'],
    queryFn: getInboxUnreadCount,
    staleTime: 30_000,
    refetchInterval: 60_000,
    // 401 on a logged-out tab shouldn't keep firing — silent on auth fail.
    retry: false,
  });

  useEffect(() => {
    const onNewMessage = (): void => {
      void qc.invalidateQueries({ queryKey: ['inbox', 'unread-count'] });
    };
    on('inbox:new_message', onNewMessage);
    return () => {
      off('inbox:new_message', onNewMessage);
    };
  }, [on, off, qc]);

  return data?.count ?? 0;
}
